/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { ClockIcon } from '@heroicons/react/24/outline'
import { Calendar, ClockRotateRight, WarningHexagon } from 'iconoir-react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { InlineAddActionLink } from 'app/components/shared/InlineAddActionLink'
import { OverflowCountChip } from 'app/components/shared/OverflowCountChip'
import { ChipMeasureRow } from 'app/components/shared/ChipMeasureRow'
import { CHIP_OVERFLOW_ITEM, useChipOverflow } from 'app/hooks/useChipOverflow'
import { TaskListPopover } from 'app/components/contacts/TaskListPopover'
import { QuickCreateTaskModal } from 'app/components/tasks/QuickCreateTaskModal'
import { UPDATE_TASK_MUTATION } from 'app/components/tasks/graphql'
import { Tooltip } from 'app/components/Tooltip'
import type { ContactJson } from 'app/types/entities'
import type { TaskJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { createBatchLoader } from 'app/lib/batchLoader'
import { TaskBadge, TaskBadgeStatus, taskBadges } from 'app/lib/taskBadges'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { debug } from 'lib/logging'

const taskBatchLoader = createBatchLoader<string, TaskJson[]>(async (contactIds) => {
  const uniqueIds = [...new Set(contactIds)]
  const searchValue = uniqueIds.map((id) => `"${id}"`).join(' OR ')
  const result = await gqlFetchList<TaskJson>(
    'tasks',
    { search: `contactId:(${searchValue})`, pagination: { limit: 1000 } },
    'id contactId status dueDate title',
  )
  const map = new Map<string, TaskJson[]>()
  for (const id of uniqueIds) map.set(id, [])
  for (const task of result.hits) {
    const list = map.get(task.contactId!)
    if (list) list.push(task)
    else map.set(task.contactId!, [task])
  }
  return map
})

export { taskBatchLoader }

const MODAL_OPTIONS = {
  hideTitle: true,
  isFullScreen: false,
  overflowHidden: false,
  removePadding: true,
  useRadix: true,
}

export function LazyTaskStatusCell({
  contact,
  onOpenContact,
}: {
  contact: ContactJson
  /** Opens the contact drawer (the page's own handler); URL params alone don't. */
  onOpenContact?: () => void
}): ReactElement | null {
  const [tasks, setTasks] = useState<TaskJson[] | undefined>(undefined)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    taskBatchLoader.load(contact.id!).then((result) => {
      if (!cancelled) setTasks(result ?? [])
    })
    return () => {
      cancelled = true
    }
  }, [contact.id, reloadKey])

  const contactTasks = useMemo(() => (tasks ?? []).filter((t) => t.status === 'open'), [tasks])

  // Optimistic single-task update with rollback — the TasksTable pattern, applied
  // to this cell's local task state. Only changed fields go in the input.
  const applyTaskUpdate = async (
    taskId: string,
    input: { status?: string; dueDate?: string },
    successMessage: string,
  ) => {
    const prev = tasks
    setTasks((current) => (current ?? []).map((t) => (t.id === taskId ? { ...t, ...input } : t)))
    taskBatchLoader.invalidate(contact.id!)
    const result = await gqlMutation(UPDATE_TASK_MUTATION, { id: taskId, input })
    if (result.error) {
      setTasks(prev)
      notifyError(`Failed to update task`, `task-update-error`)
    } else {
      notifySuccess(successMessage)
    }
  }

  const completeTask = (taskId: string) => applyTaskUpdate(taskId, { status: 'closed' }, `Task marked complete`)
  const rescheduleTask = (taskId: string, dueDate: string) => applyTaskUpdate(taskId, { dueDate }, `Task rescheduled`)

  const openModal = () => {
    debug(['contacts', 'tasks'], 'create a new task for contact', contact?.id)
    showModal(
      <QuickCreateTaskModal
        defaultContact={contact}
        onTaskCreatedOrUpdated={(task) => {
          if (task) {
            // Instant feedback + a real refetch (invalidate alone never re-runs the effect).
            setTasks((current) => [...(current ?? []), task])
            taskBatchLoader.invalidate(contact.id!)
            setReloadKey((k) => k + 1)
          }
        }}
      />,
      MODAL_OPTIONS,
    )
  }

  // Mixed statuses: one badge per bucket. Single status: one badge total.
  // The wording and the day-not-instant arithmetic live in app/lib/taskBadges,
  // shared with the Action Center's cards so the two cannot drift apart.
  const badges: TaskBadge[] = taskBadges(contactTasks)

  // Shared measurement-based clipping; reserved space covers the trailing "+" button.
  // Hooks stay above the early return below (contactTasks is [] while tasks load).
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const { visibleCount } = useChipOverflow({ containerRef, measureRef, itemCount: badges.length, reservedWidth: 30 })

  if (tasks === undefined) return null

  const getBadgeStyles = (status: TaskBadgeStatus) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-600/8 text-red-900 hover:bg-red-600/15'
      case 'dueToday':
        return 'bg-blue-600/8 text-blue-800 hover:bg-blue-600/15'
      case 'upcoming':
        return 'bg-surface-muted text-default hover:bg-surface-strong'
      case 'noDueDate':
        return 'bg-surface-muted text-subtle hover:bg-surface-strong'
    }
  }

  const getBadgeIcon = (status: TaskBadgeStatus) => {
    const className = 'h-3 w-auto shrink-0'
    switch (status) {
      case 'overdue':
        return <WarningHexagon className={className} />
      case 'dueToday':
        return <ClockIcon className={className} />
      case 'upcoming':
        return <ClockRotateRight className={className} />
      case 'noDueDate':
        return <Calendar className={className} />
    }
  }

  const badgeChip = (badge: TaskBadge) => (
    <div
      className={`tag inline-flex shrink-0 cursor-pointer items-center gap-1 rounded px-1.5 transition-colors ${getBadgeStyles(badge.status)}`}
    >
      {getBadgeIcon(badge.status)}
      <span className="whitespace-nowrap text-label-xs">{badge.text}</span>
    </div>
  )

  return contactTasks.length > 0 ? (
    <div ref={containerRef} className="relative flex min-w-0 items-center gap-1 overflow-hidden px-2">
      <TaskListPopover
        tasks={contactTasks}
        contactId={contact.id!}
        onComplete={completeTask}
        onReschedule={rescheduleTask}
        onOpenContact={onOpenContact}
        triggerClassName="flex cursor-pointer items-center gap-1 focus:outline-none"
      >
        {badges.slice(0, visibleCount).map((badge, index) => (
          <Tooltip key={index} className="flex" message={badge.tooltip}>
            {/* cursor-pointer on the badge itself: the Tooltip wrapper span hardcodes
                cursor-default, which would otherwise override the trigger's cursor. */}
            {badgeChip(badge)}
          </Tooltip>
        ))}
        <OverflowCountChip items={badges.slice(visibleCount).map((badge) => badge.text)} />
      </TaskListPopover>
      {/* Hidden always-complete copy the overflow hook measures. */}
      <ChipMeasureRow measureRef={measureRef}>
        {badges.map((badge, index) => (
          <span key={index} {...{ [CHIP_OVERFLOW_ITEM]: `` }} className="flex shrink-0 items-center">
            {badgeChip(badge)}
          </span>
        ))}
      </ChipMeasureRow>
      <button
        type="button"
        aria-label="Add task"
        title="Add task"
        onClick={(e) => {
          e.stopPropagation()
          openModal()
        }}
        className="inline-flex h-5 shrink-0 cursor-pointer items-center rounded border-[0.5px] border-strong bg-surface px-1.5 text-label-xs text-subtle transition-colors hover:border-(--fg-minimal) hover:text-default focus:outline-none"
      >
        +
      </button>
    </div>
  ) : (
    <InlineAddActionLink label="+ Add new task" onClick={() => openModal()} />
  )
}
